export interface IReferenceItem {
  id: number;
  name: string;
}

export interface IResolvedReference {
  id: number | null;
  usedFallback: boolean;
  label: string | null;
}

const normalizeReferenceText = (value?: string | null) =>
  (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const findReference = (
  list: IReferenceItem[] | undefined,
  value?: string | null
) => {
  const normalized = normalizeReferenceText(value);
  if (!list || !normalized) return null;
  return (
    list.find((item) => normalizeReferenceText(item.name) === normalized) ?? null
  );
};

export const resolveReferenceId = (
  list: IReferenceItem[] | undefined,
  value?: string | null,
  aliases: Record<string, string> = {},
  fallbackNames: string[] = ["Other"]
): IResolvedReference => {
  const direct = findReference(list, value);
  if (direct) {
    return { id: direct.id, usedFallback: false, label: direct.name };
  }

  const aliasTarget = aliases[normalizeReferenceText(value)];
  const alias = findReference(list, aliasTarget);
  if (alias) {
    return { id: alias.id, usedFallback: false, label: alias.name };
  }

  for (const fallbackName of fallbackNames) {
    const fallback = findReference(list, fallbackName);
    if (fallback) {
      return { id: fallback.id, usedFallback: true, label: fallback.name };
    }
  }

  return { id: null, usedFallback: false, label: null };
};

export const incentiveCategoryAliases: Record<string, string> = {
  "battery storage": "Battery",
  bess: "Battery",
  storage: "Battery",
  "energy storage": "Battery",
  "ev charging": "EV Charging",
  "ev charger": "EV Charging",
  "ev infrastructure": "EV Charging",
  "electric vehicle": "EV Charging",
  electrification: "Building",
  "energy efficiency": "Building",
  hvac: "Building",
  solar: "Power Generation",
  renewable: "Power Generation",
  renewables: "Power Generation",
  "power generation": "Power Generation",
  carbon: "Carbon",
  decarbonization: "Carbon",
};

export const incentiveAreaAliases: Record<string, string> = {
  der: "DER",
  distributed: "DER",
  "distributed energy resource": "DER",
  decarbonization: "Decarbonization",
  efficiency: "Efficiency",
  "energy efficiency": "Efficiency",
  electrification: "Electrification",
  renewable: "Renewables",
  renewables: "Renewables",
  solar: "Renewables",
};

export const incentiveTypeAliases: Record<string, string> = {
  rebate: "Rebate",
  rebates: "Rebate",
  incentive: "Rebate",
  incentives: "Rebate",
  grant: "Grant",
  grants: "Grant",
  "free service": "Free Service",
  "technical assistance": "Technical Assistance",
  assistance: "Technical Assistance",
};
